'use client'
import clsx from 'clsx'
import { useTranslation } from 'react-i18next'
import type { ButtonBlockProps } from '@/blocks/ButtonBlock'
import { MenuItemBlock, type MenuItemBlockProps } from '@/blocks/HeaderBlock/MenuItemBlock'
import { ButtonNextLink } from '@/components/ButtonNextLink'
import { ProductNavigationLink } from '@/components/Header/NavigationLink/NavigationLink'
import { useProductMetadata } from '@/components/LayoutWithMenu/productMetadataHooks'
import type { ExpectedBlockType, SbBaseBlockProps } from '@/services/storyblok/storyblok'
import { filterByBlockType, getLinkFieldURL } from '@/services/storyblok/Storyblok.helpers'
import { useRoutingLocale } from '@/utils/l10n/useRoutingLocale'
import { PageLink } from '@/utils/PageLink'
import * as NavigationMenuPrimitive from '@radix-ui/react-navigation-menu'
import { storyblokEditable } from '@storyblok/react'
import { Button } from 'ui/src/components/Button/Button'
import { sprinkles } from 'ui/src/theme/sprinkles.css'
import {
  navigationContent,
  navigationItem,
  navigationItemProductMenu,
  navigationMenuWrapper,
  navigationProductList,
} from '@/components/Header/Header.css'

export type ProductMenuBlockProps = SbBaseBlockProps<{
  name: string
  menuItems: ExpectedBlockType<MenuItemBlockProps>
  buttons: ExpectedBlockType<ButtonBlockProps>
  currentActiveItem?: string
}> & { variant: 'mobile' | 'desktop' }

type ProductMenuItem = { name: string; url: string; image?: string; label?: string }

export const ProductMenuBlock = ({ blok, variant }: ProductMenuBlockProps) => {
  const locale = useRoutingLocale()
  const { t } = useTranslation()
  const productMetadata = useProductMetadata()
  const filteredMenuItems = filterByBlockType(blok.menuItems, MenuItemBlock.blockName)

  const productMenuItems: Array<ProductMenuItem> = []
  filteredMenuItems.forEach((item) => {
    const product = productMetadata?.find((product) => product.name === item.name)
    let menuItem
    if (product) {
      menuItem = {
        name: product.displayNameShort,
        url: product.pageLink,
        image: product.pillowImage.src,
        label: item.label,
      }
    } else {
      menuItem = {
        name: item.name,
        url: getLinkFieldURL(item.link, item.name),
        image: item.pillowImage?.filename,
        label: item.label,
      }
    }
    productMenuItems.push(menuItem)
  })

  const content = (
    <ul className={navigationProductList}>
      {productMenuItems.map((item) => (
        <li key={item.name}>
          <ProductNavigationLink href={item.url} pillowImageSrc={item.image} label={item.label}>
            {item.name}
          </ProductNavigationLink>
        </li>
      ))}

      <li className={sprinkles({ paddingTop: 'xs' })}>
        <NavigationMenuPrimitive.Link asChild>
          <ButtonNextLink href={PageLink.store({ locale })} variant="secondary" size="medium" fullWidth={true}>
            {t('NAVIGATION_STORE_LINK')}
          </ButtonNextLink>
        </NavigationMenuPrimitive.Link>
      </li>
    </ul>
  )

  if (variant === 'mobile') {
    return <div className={sprinkles({ paddingTop: 'md' })}>{content}</div>
  } else {
    return (
      <NavigationMenuPrimitive.Item
        className={clsx(navigationItem, navigationItemProductMenu)}
        value={blok.name}
        {...storyblokEditable(blok)}
      >
        <NavigationMenuPrimitive.Trigger asChild>
          {/* Cursor is set to auto here because this button serves only as a mouse hover trigger for the dropdown menu */}
          <Button style={{ cursor: 'auto' }} size="medium" variant="secondary">
            {blok.name}
          </Button>
        </NavigationMenuPrimitive.Trigger>

        <NavigationMenuPrimitive.Content className={clsx(navigationContent, navigationMenuWrapper)}>
          {content}
        </NavigationMenuPrimitive.Content>
      </NavigationMenuPrimitive.Item>
    )
  }
}
ProductMenuBlock.blockName = 'productMenu'
